import type {
  ContractorGetCampaignModel,
  ManagerGetCampaignModel,
  ManagerUpdateCampaignModel,
} from 'entities/campaign';
import { getBidRangeData, useGetManagerTestLeadCrmHistoryJSON } from 'entities/campaign';
import { formatPrice, getValueFromString } from 'shared/lib/formatting';
import { centsToDollars } from 'shared/lib/format';
import { getHistoryTableColumns, mapCampaignEventToChangeView } from 'features/show-history';
import type { DataLayerEventName } from 'shared/lib/gtm';
import { getBudgetChangeMetricsData, pushToDataLayer } from 'shared/lib/gtm';
import type { AuthenticatedContractorModel } from 'entities/profile';
import { isBoolean } from 'shared/lib/type-guards';

import { CAMPAIGN_HISTORY_EVENTS_BADGE_PROPS } from '../constants';
import type { CampaignEditModalBodyProps, CampaignEditModel } from '../model';

export function getCampaignFormValues(campaign: ManagerGetCampaignModel | ContractorGetCampaignModel) {
  let formValues: CampaignEditModel = {
    type: campaign.type,
    name: campaign.name,
    pausedReason: campaign.pausedReasonId ? campaign.pausedReasonId.toString() : '',
  };

  if (campaign.budget) {
    formValues.budget = formatPrice(campaign.budget.amount);
  }

  if (campaign.minimalIncome) {
    formValues.minimalIncome = formatPrice(campaign.minimalIncome.amount);
  }

  if (campaign.bid) {
    formValues.bid = `$${centsToDollars(campaign.bid)}`;
  }

  if (campaign.bidTierGroups?.length) {
    formValues.bidTiers = campaign.bidTierGroups[0].bidTiers.map((value) => ({
      bid: `$${centsToDollars(value.campaignBidTier.bid)}`,
      id: value.id,
    }));
  }

  if (isManagerCampaignModel(campaign)) {
    formValues = {
      ...formValues,
      isDeactivated: campaign.status === 'deactivated',
      simplybookUrl: campaign.simplybookUrl,
      contractor: campaign.contractor.companyName,
    };
  }

  if (campaign.schedule) {
    formValues = {
      ...formValues,
      ...campaign.schedule,
    };
  }

  if (isManagerCampaignModel(campaign) && isBoolean(campaign.acceptOtherServices)) {
    formValues = {
      ...formValues,
      acceptOtherServices: campaign.acceptOtherServices,
    };
  }

  return {
    defaultValues: formValues,
  };
}

export function getUpdateCampaignParams({
  form,
  campaign,
}: {
  form: CampaignEditModel;
  campaign: ManagerGetCampaignModel | ContractorGetCampaignModel;
}) {
  const isContractorSalesCampaign = !isManagerCampaignModel(campaign) && campaign.type === 'sales';
  const params: ManagerUpdateCampaignModel = {
    name: form.name,
    serviceId: campaign.service.id,
    ...(!isContractorSalesCampaign ? getBidRangeData({ bid: form.bid, bidTiers: form.bidTiers }) : {}),
  };

  if (form.budget) {
    params.budget = getValueFromString(form.budget);
  }
  if (form.minimalIncome) {
    params.minimalIncome = getValueFromString(form.minimalIncome);
  }
  if (form.simplybookUrl) {
    params.simplybookUrl = form.simplybookUrl;
  }
  if (isBoolean(form.acceptOtherServices)) {
    params.acceptOtherServices = form.acceptOtherServices;
  }
  if (form.dbaId) {
    params.dbaId = Number(form.dbaId);
  }
  if (!campaign.isActive) {
    params.pausedReasonId = form.pausedReason ? Number(form.pausedReason) : null;
  }

  return params;
}

export function isShowSimplybookUrl(campaign: ManagerGetCampaignModel | ContractorGetCampaignModel) {
  return isManagerCampaignModel(campaign) && campaign.type === 'appt';
}

export function isManagerCampaignModel(
  value: ManagerGetCampaignModel | ContractorGetCampaignModel
): value is ManagerGetCampaignModel {
  return 'simplybookUrl' in value && 'contractor' in value;
}

const MIN_BUDGET = 50000;
const MAX_BUDGET = 1000000;
const BUDGET_PERCENTAGE_THRESHOLD = 40;

export function isBudgetChangeAlertShown(
  budget?: string,
  initialBudget?: string,
  minBudget = MIN_BUDGET
): budget is string {
  if (budget && initialBudget) {
    const budgetValue = getValueFromString(budget);
    const initialBudgetValue = getValueFromString(formatPrice(initialBudget));

    const isWithinRange = budgetValue >= centsToDollars(minBudget) && budgetValue <= MAX_BUDGET;

    if (isWithinRange) {
      const percentageDifference = Math.abs((100 * budgetValue) / initialBudgetValue);

      return Math.abs(percentageDifference - 100) >= BUDGET_PERCENTAGE_THRESHOLD;
    }
  }

  return false;
}

export const historyTableColumns = (id: number) =>
  getHistoryTableColumns({
    changeDetailMapper: mapCampaignEventToChangeView,
    badgePropsConfig: CAMPAIGN_HISTORY_EVENTS_BADGE_PROPS,
    withUserColumn: true,
    withSourceTooltip: true,
    withJsonColumn: {
      entityId: id,
      jsonDownloader: useGetManagerTestLeadCrmHistoryJSON,
    },
    columnClassNames: {
      userColumnClassName: 'max-w-[85px]',
      eventColumnClassName: 'max-w-[185px]',
    },
  });

export function getIsBudgetIncrease(budget: string | undefined, currentBudget: number) {
  const budgetFormatted = centsToDollars(Number(budget));

  return currentBudget > budgetFormatted;
}

export const sendBudgetChangeEvent = ({
  event,
  newBudget = '-1',
  userData,
  campaignData,
}: {
  event: DataLayerEventName;
  newBudget?: string;
  userData?: AuthenticatedContractorModel;
  campaignData: CampaignEditModalBodyProps['campaign'];
}) => {
  const metricsToPush = getBudgetChangeMetricsData({
    contractorId: userData?.id as number,
    contractorEmail: userData?.email as string,
    campaignId: campaignData.id,
    campaignName: campaignData.name,
    campaignType: campaignData.type,
    oldBudget: campaignData.budget?.amount as string,
    newBudget: getValueFromString(newBudget),
  });

  pushToDataLayer({
    event,
    ...metricsToPush,
  });
};
